import React, { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import { FaUpload, FaDownload, FaTrash, FaEye, FaEdit, FaTimes, FaSpinner, FaShare, FaCloudDownloadAlt, FaSave, FaBars, FaFileAlt, FaPhone, FaEnvelope, FaCalendar, FaPaperPlane, FaBan, FaPlus, FaChevronDown, FaCog } from 'react-icons/fa';
import { MdOutlineRefresh } from "react-icons/md";
import { useAuth } from '../../contexts/AuthContext';
import { usePlan } from '../../contexts/PlanContext';
import { useToast } from '../../contexts/ToastContext';
import { uploadCSV, previewCSV as previewCSVService, deleteAccount, importFromHubSpot, getHubSpotPreview, updateAccount } from '../../services/radar';
import { Button, Input, Select, Table, Modal, Card, Badge } from '../shared';
import FieldMappingModal from './FieldMappingModal';

const RadarAccounts = memo(({ 
  accounts = [],
  industries, 
  dealStages,
  loading = false,
  pagination = null,
  requestParams = {},
  onLoadAccounts
}) => {
  const { user } = useAuth();
  const { planType } = usePlan();
  const { showError, showSuccess, showWarning } = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    industry: '',
    dealStage: ''
  });

  useEffect(() => {
    if (requestParams) {
      setSearchTerm(requestParams.search ?? '');
      setFilters({
        industry: requestParams.industry ?? '',
        dealStage: requestParams.dealStage ?? ''
      });
    }
  }, [requestParams?.search, requestParams?.industry, requestParams?.dealStage]);

  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);

  // Field mapping state
  const [showFieldMapping, setShowFieldMapping] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvSampleRow, setCsvSampleRow] = useState(null);
  const [fieldMappings, setFieldMappings] = useState({});
  const [customFields, setCustomFields] = useState({});

  // Import state
  const [isHubSpotImporting, setIsHubSpotImporting] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isHubSpotPreviewUpdating, setIsHubSpotPreviewUpdating] = useState(false);
  const [showHubSpotPreview, setShowHubSpotPreview] = useState(false);
  const [hubSpotPreviewData, setHubSpotPreviewData] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [showHubSpotFieldMapping, setShowHubSpotFieldMapping] = useState(false);
  const [hubSpotPreviewSearchTerm, setHubSpotPreviewSearchTerm] = useState('');
  const [hubSpotPreviewPage, setHubSpotPreviewPage] = useState(1);
  const [hubSpotPreviewTotalPages, setHubSpotPreviewTotalPages] = useState(1);
  const hubSpotPreviewPageSize = 50;

  // Dropdown state
  const dropdownRef = useRef(null);
  const [showAddAccountsDropdown, setShowAddAccountsDropdown] = useState(false);

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);

  // Delete confirmation state
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Removed error and success states - now using toast system

  // Handle click outside Add Accounts dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAddAccountsDropdown && dropdownRef.current) {
        if (!dropdownRef.current.contains(event.target)) {
          setShowAddAccountsDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAddAccountsDropdown]);

  // Data loading helpers
  const buildLoadParams = useCallback((overrides = {}) => ({
    search: overrides.search ?? searchTerm,
    industry: overrides.industry ?? filters.industry,
    dealStage: overrides.dealStage ?? filters.dealStage,
    page: overrides.page ?? requestParams.page ?? 1,
    limit: overrides.limit ?? requestParams.limit ?? 25
  }), [filters, searchTerm, requestParams]);

  const reloadAccounts = useCallback((overrides = {}) => {
    if (typeof onLoadAccounts === 'function') {
      onLoadAccounts(buildLoadParams(overrides));
    }
  }, [onLoadAccounts, buildLoadParams]);

  // Search and filter handlers
  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleFilterChange = useCallback((filter, value) => {
    setFilters(prev => ({
      ...prev,
      [filter]: value
    }));
  }, []);

  const applyFilters = useCallback(() => {
    reloadAccounts({ page: 1 });
  }, [reloadAccounts]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setFilters({
      industry: '',
      dealStage: ''
    });
    reloadAccounts({ search: '', industry: '', dealStage: '', page: 1 });
  }, [reloadAccounts]);

  // Memoized options for better performance
  const industryOptions = useMemo(() => 
    industries.map(industry => ({ value: industry, label: industry })), 
    [industries]
  );

  const dealStageOptions = useMemo(() => 
    dealStages.map(stage => ({ value: stage, label: stage })), 
    [dealStages]
  );

  // File upload handlers
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const input = event.target;
    setSelectedFile(file);

    try {
      setIsLoadingPreview(true);
      const previewResult = await previewCSVService(file);
      
      if (previewResult?.status === 'success' && previewResult.data) {
        const { headers, sampleRows } = previewResult.data;
        setCsvHeaders(headers);
        setCsvSampleRow(sampleRows && sampleRows.length > 0 ? sampleRows[0] : null);
        setShowFieldMapping(true);
      } else {
        showError('Failed to preview CSV file');
      }
    } catch (error) {
      console.error('Preview error:', error);
      showError(error.message || 'Failed to preview CSV file');
    } finally {
      setIsLoadingPreview(false);
      // Reset input so users can select the same file again if needed
      if (input) {
        input.value = '';
      }
    }
  };

  const handleFieldMappingConfirm = async (mappingData) => {
    setFieldMappings(mappingData.mappings);
    setCustomFields(mappingData.customFields);
    setShowFieldMapping(false);
    
    // Proceed with upload using the mappings
    await handleUpload(selectedFile, mappingData.mappings, mappingData.customFields);
  };

  const handleUpload = async (fileToUpload = selectedFile, mappings = fieldMappings, customFieldsData = customFields) => {
    if (!fileToUpload) {
      showError('Please select a CSV file to upload');
      return;
    }

    setIsUploading(true);
    
    try {
      const response = await uploadCSV(fileToUpload, mappings, customFieldsData);
      
      if (response?.status === 'success') {
        const { totalProcessed, successCount } = response.data || {};
        const processedMessage = successCount !== undefined && totalProcessed !== undefined
          ? `Uploaded ${successCount}/${totalProcessed} accounts successfully`
          : 'Accounts uploaded successfully';
        showSuccess(processedMessage);
        
        // Refresh the accounts list
        await reloadAccounts();
        
        setSelectedFile(null);
        setPreviewData(null);
        setValidationErrors([]);
        setFieldMappings({});
        setCustomFields({});
      } else {
        throw new Error(response?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showError(error.message || 'Failed to upload file', {
        actions: [
          { label: 'Retry', primary: true, onClick: () => handleUpload(fileToUpload, mappings, customFieldsData) }
        ]
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Import handlers
  const handleHubSpotImport = async () => {
    try {
      setIsLoadingPreview(true);
      setHubSpotPreviewSearchTerm('');
      setHubSpotPreviewPage(1);
      
      const response = await getHubSpotPreview({ page: 1, pageSize: hubSpotPreviewPageSize, q: '' });
      setHubSpotPreviewData(response);
      setHubSpotPreviewPage(response?.data?.page || 1);
      setHubSpotPreviewTotalPages(response?.data?.totalPages || 1);
      setSelectedCompanies([]);
      setShowHubSpotPreview(true);
    } catch (error) {
      console.error('HubSpot preview error:', error);
      showError(error.message || 'Failed to get HubSpot preview', {
        actions: [
          { label: 'Retry', primary: true, onClick: handleHubSpotImport }
        ]
      });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const fetchHubSpotPreviewPage = async (pageToLoad, search = hubSpotPreviewSearchTerm, { soft = false } = {}) => {
    try {
      if (soft) {
        setIsHubSpotPreviewUpdating(true);
      } else {
        setIsLoadingPreview(true);
      }
      const response = await getHubSpotPreview({
        page: pageToLoad,
        pageSize: hubSpotPreviewPageSize,
        q: (search || '').trim()
      });
      setHubSpotPreviewData(response);
      setHubSpotPreviewPage(response?.data?.page || pageToLoad);
      setHubSpotPreviewTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error('HubSpot preview page fetch error:', error);
      showError(error.message || 'Failed to load HubSpot accounts');
    } finally {
      setIsLoadingPreview(false);
      setIsHubSpotPreviewUpdating(false);
    }
  };

  // Debounced server-side search for the preview overlay
  useEffect(() => {
    if (!showHubSpotPreview) return;
    const t = setTimeout(() => {
      fetchHubSpotPreviewPage(1, hubSpotPreviewSearchTerm, { soft: true });
      setSelectedCompanies([]);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubSpotPreviewSearchTerm, showHubSpotPreview]);

  const handleHubSpotImportConfirm = async () => {
    try {
      if (selectedCompanies.length === 0) {
        showError('Please select at least one company to import');
        return;
      }

      // Show field mapping modal for HubSpot import
      if (hubSpotPreviewData?.data?.companies?.length > 0) {
        const firstCompany = hubSpotPreviewData.data.companies[0];
        console.log('First company data:', firstCompany);
        
        // Extract all HubSpot properties from the first company
        const hubspotProperties = firstCompany.properties || {};
        console.log('HubSpot properties:', hubspotProperties);
        
        // Get all property keys (including empty ones for mapping purposes)
        const hubspotFields = Object.keys(hubspotProperties);
        
        console.log('Extracted HubSpot fields:', hubspotFields);
        
        // If no properties found, use common HubSpot field names as fallback
        if (hubspotFields.length === 0) {
          console.warn('No properties found, using fallback fields');
          const commonFields = ['name', 'domain', 'industry', 'description', 'phone', 'address', 'website', 'numberofemployees'];
          setCsvHeaders(commonFields);
        } else {
          setCsvHeaders(hubspotFields);
        }
        
        setCsvSampleRow(firstCompany);
        setShowHubSpotFieldMapping(true);
      } else {
        // Proceed without mapping if no preview data
        await proceedWithHubSpotImport({}, {});
      }
    } catch (error) {
      console.error('Error in handleHubSpotImportConfirm:', error);
      showError(error.message || 'Failed to process HubSpot import');
    }
  };

  const handleHubSpotFieldMappingConfirm = async (mappingData) => {
    setShowHubSpotFieldMapping(false);
    await proceedWithHubSpotImport(mappingData.mappings, mappingData.customFields);
  };

  const proceedWithHubSpotImport = async (mappings = {}, customFieldsData = {}) => {
    try {
      setIsHubSpotImporting(true);
      
      const response = await importFromHubSpot(selectedCompanies, mappings, customFieldsData);
      
      setShowHubSpotPreview(false);
      setHubSpotPreviewData(null);
      setSelectedCompanies([]);
      
      await reloadAccounts();
      showSuccess('Accounts imported successfully from HubSpot');
    } catch (error) {
      console.error('HubSpot import error:', error);
      showError(error.message || 'Failed to import accounts from HubSpot', {
        actions: [
          { label: 'Retry', primary: true, onClick: () => proceedWithHubSpotImport(mappings, customFieldsData) }
        ]
      });
    } finally {
      setIsHubSpotImporting(false);
    }
  };

  // Account management handlers
  const handleDeleteClick = (accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    setAccountToDelete({ id: accountId, name: account?.name || account?.companyName || 'this account' });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!accountToDelete) return;
    
    try {
      setIsDeleting(true);
      await deleteAccount(accountToDelete.id);
      await reloadAccounts();
      setShowDeleteModal(false);
      setAccountToDelete(null);
      showSuccess('Account deleted successfully');
    } catch (err) {
      console.error('Error deleting account:', err);
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to delete account';
      showError(errorMessage, {
        actions: [
          { label: 'Retry', primary: true, onClick: handleDeleteConfirm }
        ]
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setAccountToDelete(null);
  };

  const exportAccounts = () => {
    const csvContent = [
      ['Company Name', 'Industry', 'Deal Stage', 'Company Size', 'Website'],
      ...accounts.map(account => [
        account.name || '',
        account.industry || '',
        account.dealStage || '',
        account.companySize || '',
        account.website || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-accounts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Dropdown handlers
  const toggleAddAccountsDropdown = () => {
    setShowAddAccountsDropdown(!showAddAccountsDropdown);
  };

  const closeAddAccountsDropdown = () => {
    setShowAddAccountsDropdown(false);
  };

  const handleUploadAccounts = () => {
    closeAddAccountsDropdown();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleHubSpotImportClick = () => {
    closeAddAccountsDropdown();
    handleHubSpotImport();
  };

  // Inline editing handlers
  const handleStartEdit = (accountId, field, value) => {
    setEditingCell({ accountId, field });
    setEditValue(value || '');
  };

  const handleSaveEdit = async (accountId, field) => {
    try {
      setSavingCell({ accountId, field });
      
      const updateData = {
        [field]: editValue
      };

      await updateAccount(accountId, updateData);
      await reloadAccounts();

      setEditingCell(null);
      setEditValue('');
      setSavingCell(null);
      showSuccess('Account updated successfully');
    } catch (error) {
      console.error('Error updating account:', error);
      // Extract error message from Error object or response
      const errorMessage = error?.message || error?.response?.data?.message || 'Failed to update account';
      showError(errorMessage, {
        actions: [
          { label: 'Retry', primary: true, onClick: () => handleSaveEdit(accountId, field) }
        ]
      });
      // Keep editing state active so user can fix the issue
      setSavingCell(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleEditKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit(editingCell.accountId, editingCell.field);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Render cell content with inline editing
  const renderCell = (account, field) => {
    const value = account[field] || '';
    
    if (editingCell && editingCell.accountId === account.id && editingCell.field === field) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyPress={handleEditKeyPress}
            onBlur={() => handleSaveEdit(account.id, field)}
            style={{ 
              padding: '2px 4px', 
              fontSize: '12px', 
              border: '1px solid #3b82f6', 
              borderRadius: '2px',
              width: '100%'
            }}
            autoFocus
          />
          {savingCell && savingCell.accountId === account.id && savingCell.field === field && (
            <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '10px' }} />
          )}
        </div>
      );
    }
    
    return (
      <div 
        style={{ cursor: 'pointer', padding: '2px' }}
        onClick={() => handleStartEdit(account.id, field, value)}
        title="Click to edit"
      >
        {value || 'N/A'}
      </div>
    );
  };

  const currentPage = pagination?.currentPage ?? requestParams.page ?? 1;
  const totalPages = pagination?.totalPages ?? null;
  const itemsPerPage = pagination?.itemsPerPage ?? requestParams.limit ?? 25;
  const canGoPrev = currentPage > 1;
  const canGoNext = totalPages ? currentPage < totalPages : accounts.length === itemsPerPage;

  const visibleHubSpotPreviewCompanies = useMemo(() => {
    const list = hubSpotPreviewData?.data?.companies;
    if (!Array.isArray(list)) return [];
    const q = (hubSpotPreviewSearchTerm || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter(c => {
      const blob = `${c?.name || ''} ${c?.website || ''} ${c?.industry || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [hubSpotPreviewData, hubSpotPreviewSearchTerm]);

  const visibleHubSpotPreviewCompanyIds = useMemo(
    () => visibleHubSpotPreviewCompanies.map(c => c.hubspotId),
    [visibleHubSpotPreviewCompanies]
  );

  const allVisibleHubSpotPreviewCompaniesSelected =
    visibleHubSpotPreviewCompanyIds.length > 0 &&
    visibleHubSpotPreviewCompanyIds.every(id => selectedCompanies.includes(id));

  return (
    <div className="radar-content">
      <div style={{ display: 'flex', gap: '20px', height: '100%' }}>
        {/* Left Side - Search and Filters */}
        <div className="radar-accounts-left-pane" style={{ width: '250px', marginLeft: '20px', height: '100%' }}>
          <div className="search-filters-section" style={{ display: 'flex', flexDirection: 'column', padding: '5px' }}>
            
            <div className="radar-search-box" style={{ display: 'flex', flexDirection: 'column' }}>
              <input
                type="text"
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={handleSearch}
                className="radar-search-input"
                style={{ width: '100%', marginBottom: '10px' }}
              />
              <button onClick={() => {
                applyFilters();
              }} className="radar-search-button" style={{ width: '100%' }}>
                Search
              </button>
            </div>
            </div>
          <h3 style={{ marginTop: '20px', fontSize: '18px', fontWeight: '600', color: '#1f2937', borderTop: '1px solid #e5e7eb' }}>Filter</h3>  
          <div className="filters" style={{ marginBottom: '20px', overflow: 'visible', position: 'relative', zIndex: 1 }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Industry</label>
            <select
              value={filters.industry}
              onChange={(e) => handleFilterChange('industry', e.target.value)}
              className="filter-select"
              style={{ 
                width: '100%', 
                marginBottom: '15px',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer',
                appearance: 'auto',
                WebkitAppearance: 'menulist',
                MozAppearance: 'menulist',
                position: 'relative',
                zIndex: 10
              }}
            >
              <option value="">All Industries</option>
              {industries && industries.length > 0 ? (
                industries.map(industry => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))
              ) : (
                <option value="" disabled>No industries available</option>
              )}
            </select>
            
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Deal Stage</label>
            <select
              value={filters.dealStage}
              onChange={(e) => handleFilterChange('dealStage', e.target.value)}
              className="filter-select"
              style={{ 
                width: '100%', 
                marginBottom: '15px',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer',
                appearance: 'auto',
                WebkitAppearance: 'menulist',
                MozAppearance: 'menulist',
                position: 'relative',
                zIndex: 10
              }}
            >
              <option value="">All Stages</option>
              {dealStages && dealStages.length > 0 ? (
                dealStages.map(dealStage => (
                  <option key={dealStage} value={dealStage}>
                    {dealStage}
                  </option>
                ))
              ) : (
                <option value="" disabled>No deal stages available</option>
              )}
            </select>
            
            <button onClick={() => {
              clearFilters();
            }} className="clear-filters-button" style={{ width: '100%', marginBottom: '20px' }}>
              Clear Filters
            </button>
          </div>
        </div>

        {/* Right Side - Accounts Table */}
        <div className="radar-accounts-right-pane" style={{ flex: 1, background: 'transparent', marginRight: '20px', height: '80vh', width: '80%' }}>
          <div className="radar-accounts-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', width: '95%' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
              Team Account List ({(pagination?.totalItems ?? accounts.length) || 0} entries)
            </h3>
            <div className="radar-accounts-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                onClick={exportAccounts}
                style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <FaDownload />
                Export
              </button>
              
              <div className="add-accounts-dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
                <button
                  className="add-accounts-button"
                  onClick={toggleAddAccountsDropdown}
                  disabled={isLoadingPreview || isUploading || isHubSpotImporting}
                  style={{ 
                    padding: '8px 16px', 
                    background: (isLoadingPreview || isUploading || isHubSpotImporting) ? '#9ca3af' : '#3b82f6', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '6px', 
                    cursor: (isLoadingPreview || isUploading || isHubSpotImporting) ? 'not-allowed' : 'pointer', 
                    fontSize: '12px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    opacity: (isLoadingPreview || isUploading || isHubSpotImporting) ? 0.7 : 1
                  }}
                >
                  {(isLoadingPreview || isUploading || isHubSpotImporting) ? (
                    <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <FaPlus />
                  )}
                  Add Accounts
                  {!(isLoadingPreview || isUploading || isHubSpotImporting) && (
                    <FaChevronDown className={`dropdown-arrow ${showAddAccountsDropdown ? 'rotated' : ''}`} />
                  )}
                </button>
                
                {showAddAccountsDropdown && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', zIndex: 1000, minWidth: '200px' }}>
                    <button
                      onClick={handleUploadAccounts}
                      disabled={isUploading}
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        textAlign: 'left', 
                        background: isUploading ? '#f3f4f6' : 'none', 
                        border: 'none', 
                        cursor: isUploading ? 'not-allowed' : 'pointer', 
                        fontSize: '12px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        opacity: isUploading ? 0.6 : 1,
                        color: isUploading ? '#9ca3af' : 'inherit'
                      }}
                    >
                      {isUploading ? (
                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <FaUpload />
                      )}
                      Upload CSV
                    </button>
                    <button
                      onClick={handleHubSpotImportClick}
                      disabled={isHubSpotImporting || isLoadingPreview}
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        textAlign: 'left', 
                        background: (isHubSpotImporting || isLoadingPreview) ? '#f3f4f6' : 'none', 
                        border: 'none', 
                        cursor: (isHubSpotImporting || isLoadingPreview) ? 'not-allowed' : 'pointer', 
                        fontSize: '12px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        opacity: (isHubSpotImporting || isLoadingPreview) ? 0.6 : 1,
                        color: (isHubSpotImporting || isLoadingPreview) ? '#9ca3af' : 'inherit'
                      }}
                    >
                      {(isHubSpotImporting || isLoadingPreview) ? (
                        <>
                          <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                          {isLoadingPreview ? 'Loading Accounts from Hubspot' : 'Importing...'}
                        </>
                      ) : (
                        <>
                          <FaCloudDownloadAlt />
                          Import from HubSpot
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {/* Accounts Content */}
          <div className="radar-accounts-content" style={{ background: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', width: '95%', height: '70vh', overflowY: 'auto', overflowX: 'auto' }}>
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', gap: '16px' }}>
                <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '32px', color: '#3b82f6' }} />
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Loading accounts...</span>
              </div>
            ) : accounts.length > 0 ? (
              <div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Company</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Industry</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Deal Stage</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Company Size</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Website</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.map((account) => (
                        <tr key={account.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                            {renderCell(account, 'name')}
                          </td>
                          <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                            {renderCell(account, 'industry')}
                          </td>
                          <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                            {renderCell(account, 'dealStage')}
                          </td>
                          <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                            {renderCell(account, 'companySize')}
                          </td>
                          <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                            {renderCell(account, 'website')}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                onClick={() => handleDeleteClick(account.id)}
                                style={{
                                  padding: '4px',
                                  background: '#dc2626',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '10px'
                                }}
                                title="Delete Account"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages && totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => reloadAccounts({ page: currentPage - 1 })}
                        disabled={!canGoPrev}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          background: canGoPrev ? '#fff' : '#f3f4f6',
                          cursor: canGoPrev ? 'pointer' : 'not-allowed',
                          fontSize: '12px'
                        }}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => reloadAccounts({ page: currentPage + 1 })}
                        disabled={!canGoNext}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          background: canGoNext ? '#fff' : '#f3f4f6',
                          cursor: canGoNext ? 'pointer' : 'not-allowed',
                          fontSize: '12px'
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <FaFileAlt style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>No accounts found</h3>
                <p style={{ margin: '0', fontSize: '14px' }}>
                  {searchTerm || Object.values(filters).some(f => f !== '') 
                    ? 'Try adjusting your search or filters' 
                    : 'Add your first account to get started'
                  }
                </p>
              </div>
            )}
          </div>

          {/* Error and Success Messages - Now handled by Toast system */}
        </div>
      </div>

      {/* HubSpot Accounts Preview Modal */}
      {showHubSpotPreview && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => {
            setShowHubSpotPreview(false);
            setHubSpotPreviewData(null);
            setSelectedCompanies([]);
            setHubSpotPreviewSearchTerm('');
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#374151' }}>
                Import Accounts from HubSpot
              </h3>
              <button
                onClick={() => {
                  setShowHubSpotPreview(false);
                  setHubSpotPreviewData(null);
                  setSelectedCompanies([]);
                  setHubSpotPreviewSearchTerm('');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: 0,
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>

            {isLoadingPreview && !hubSpotPreviewData ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px', gap: '16px' }}>
                <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '32px', color: '#3b82f6' }} />
                <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>Loading HubSpot accounts...</p>
              </div>
            ) : hubSpotPreviewData?.data?.companies ? (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '14px' }}>
                    Select the accounts you want to import (Page {hubSpotPreviewPage} of {hubSpotPreviewTotalPages})
                  </p>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                    <input
                      type="text"
                      value={hubSpotPreviewSearchTerm}
                      onChange={(e) => setHubSpotPreviewSearchTerm(e.target.value)}
                      placeholder="Search accounts (name, website, industry)"
                      style={{
                        flex: 1,
                        padding: '8px 10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => {
                          if (allVisibleHubSpotPreviewCompaniesSelected) {
                            setSelectedCompanies(prev => prev.filter(id => !visibleHubSpotPreviewCompanyIds.includes(id)));
                          } else {
                            setSelectedCompanies(prev => Array.from(new Set([...prev, ...visibleHubSpotPreviewCompanyIds])));
                          }
                        }}
                        style={{
                          padding: '4px 12px',
                          background: '#f3f4f6',
                          color: '#374151',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {allVisibleHubSpotPreviewCompaniesSelected ? 'Deselect All' : 'Select All'}
                      </button>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {selectedCompanies.length} selected
                      </span>
                    </div>

                    {/* Pagination controls (right aligned) */}
                    {hubSpotPreviewTotalPages > 1 && (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => fetchHubSpotPreviewPage(Math.max(1, hubSpotPreviewPage - 1), hubSpotPreviewSearchTerm, { soft: true })}
                          disabled={hubSpotPreviewPage <= 1 || isLoadingPreview || isHubSpotPreviewUpdating}
                          style={{
                            padding: '6px 10px',
                            background: hubSpotPreviewPage <= 1 || isLoadingPreview || isHubSpotPreviewUpdating ? '#e5e7eb' : '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: hubSpotPreviewPage <= 1 || isLoadingPreview || isHubSpotPreviewUpdating ? 'not-allowed' : 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Prev
                        </button>
                        {Array.from({ length: hubSpotPreviewTotalPages }, (_, i) => i + 1)
                          .filter(p => p === 1 || p === hubSpotPreviewTotalPages || Math.abs(p - hubSpotPreviewPage) <= 2)
                          .reduce((acc, p, idx, arr) => {
                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((p, idx) => (
                            p === '…' ? (
                              <span key={`ellipsis-${idx}`} style={{ fontSize: '12px', color: '#6b7280', padding: '0 4px' }}>…</span>
                            ) : (
                              <button
                                key={`page-${p}`}
                              onClick={() => fetchHubSpotPreviewPage(p, hubSpotPreviewSearchTerm, { soft: true })}
                              disabled={isLoadingPreview || isHubSpotPreviewUpdating}
                                style={{
                                  padding: '6px 10px',
                                  background: p === hubSpotPreviewPage ? '#3b82f6' : '#f3f4f6',
                                  color: p === hubSpotPreviewPage ? 'white' : '#374151',
                                  border: 'none',
                                  borderRadius: '6px',
                                cursor: isLoadingPreview || isHubSpotPreviewUpdating ? 'not-allowed' : 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                {p}
                              </button>
                            )
                          ))}
                        <button
                          onClick={() => fetchHubSpotPreviewPage(Math.min(hubSpotPreviewTotalPages, hubSpotPreviewPage + 1), hubSpotPreviewSearchTerm, { soft: true })}
                          disabled={hubSpotPreviewPage >= hubSpotPreviewTotalPages || isLoadingPreview || isHubSpotPreviewUpdating}
                          style={{
                            padding: '6px 10px',
                            background: hubSpotPreviewPage >= hubSpotPreviewTotalPages || isLoadingPreview || isHubSpotPreviewUpdating ? '#e5e7eb' : '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: hubSpotPreviewPage >= hubSpotPreviewTotalPages || isLoadingPreview || isHubSpotPreviewUpdating ? 'not-allowed' : 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ position: 'relative', maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                  {isHubSpotPreviewUpdating && (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(255, 255, 255, 0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 5
                    }}>
                      <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '22px', color: '#3b82f6' }} />
                    </div>
                  )}
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={allVisibleHubSpotPreviewCompaniesSelected}
                            onChange={() => {
                              if (allVisibleHubSpotPreviewCompaniesSelected) {
                                setSelectedCompanies(prev => prev.filter(id => !visibleHubSpotPreviewCompanyIds.includes(id)));
                              } else {
                                setSelectedCompanies(prev => Array.from(new Set([...prev, ...visibleHubSpotPreviewCompanyIds])));
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Company Name</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Website</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Industry</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleHubSpotPreviewCompanies.map((company) => (
                        <tr key={company.hubspotId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px' }}>
                            <input
                              type="checkbox"
                              checked={selectedCompanies.includes(company.hubspotId)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCompanies([...selectedCompanies, company.hubspotId]);
                                } else {
                                  setSelectedCompanies(selectedCompanies.filter(id => id !== company.hubspotId));
                                }
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                            {company.name || 'N/A'}
                          </td>
                          <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                            {company.website || 'N/A'}
                          </td>
                          <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                            {company.industry || 'N/A'}
                          </td>
                          <td style={{ padding: '8px', fontSize: '12px' }}>
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: '500',
                              background: company.action === 'update' ? '#fef3c7' : '#dcfce7',
                              color: company.action === 'update' ? '#92400e' : '#166534'
                            }}>
                              {company.action === 'update' ? 'Update' : 'New'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button
                    onClick={() => {
                      setShowHubSpotPreview(false);
                      setHubSpotPreviewData(null);
                      setSelectedCompanies([]);
                      setHubSpotPreviewSearchTerm('');
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleHubSpotImportConfirm}
                    disabled={selectedCompanies.length === 0 || isHubSpotImporting}
                    style={{
                      padding: '8px 16px',
                      background: selectedCompanies.length === 0 || isHubSpotImporting ? '#d1d5db' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: selectedCompanies.length === 0 || isHubSpotImporting ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isHubSpotImporting ? (
                      <>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                        Importing...
                      </>
                    ) : (
                      `Import ${selectedCompanies.length} Account${selectedCompanies.length !== 1 ? 's' : ''}`
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <p style={{ margin: 0, fontSize: '14px' }}>No accounts found or failed to load preview</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Field Mapping Modal for CSV Upload */}
      <FieldMappingModal
        isOpen={showFieldMapping}
        onClose={() => {
          setShowFieldMapping(false);
          setSelectedFile(null);
          setCsvHeaders([]);
          setCsvSampleRow(null);
        }}
        onConfirm={handleFieldMappingConfirm}
        incomingFields={csvHeaders}
        existingFields={[]}
        sourceType="csv"
      />

      {/* Field Mapping Modal for HubSpot Import */}
      <FieldMappingModal
        isOpen={showHubSpotFieldMapping}
        onClose={() => {
          setShowHubSpotFieldMapping(false);
          setCsvHeaders([]);
          setCsvSampleRow(null);
        }}
        onConfirm={handleHubSpotFieldMappingConfirm}
        incomingFields={csvHeaders}
        existingFields={[]}
        sourceType="hubspot"
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        title="Delete Account"
      >
        <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '20px', fontSize: '14px', color: '#374151' }}>
            Are you sure you want to delete <strong>{accountToDelete?.name}</strong>? This will also permanently remove all contacts associated with this company and cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleDeleteCancel}
              variant="secondary"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="danger"
              disabled={isDeleting}
              loading={isDeleting}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
});

export default RadarAccounts;

RadarAccounts.displayName = 'RadarAccounts';
